import { i18next } from './i18n';

const currentLocale = (): string => i18next.language || 'en';

const EM_DASH = '—';

/** Short calendar date in the active locale, e.g. `17 Aug 2026`. */
export const formatDay = (value: string | number | Date | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return EM_DASH;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return EM_DASH;
  }

  return new Intl.DateTimeFormat(currentLocale(), { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};

/** Date and time in the active locale, used for `lastChecked`-style timestamps. */
export const formatDayTime = (value: string | number | Date | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return EM_DASH;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return EM_DASH;
  }

  return new Intl.DateTimeFormat(currentLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/** A distance with a localized `km` unit, e.g. `32.1 km`. */
export const formatKm = (value: number | null | undefined, maximumFractionDigits = 1): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return EM_DASH;
  }

  return new Intl.NumberFormat(currentLocale(), {
    style: 'unit',
    unit: 'kilometer',
    unitDisplay: 'short',
    maximumFractionDigits,
  }).format(value);
};

/** A plain integer count with locale grouping, e.g. `1,600`. */
export const formatCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return EM_DASH;
  }

  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(value);
};

/** A decimal number with locale grouping, e.g. `12.5`. Trailing zeros are dropped. */
export const formatDecimal = (value: number | null | undefined, maximumFractionDigits = 2): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return EM_DASH;
  }

  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits }).format(value);
};

/** A measurement with its unit appended verbatim, e.g. `12.5 L/100km`, `457 hp`. */
export const formatMeasurement = (value: number | null | undefined, unit: string, maximumFractionDigits = 2): string =>
  value === null || value === undefined || Number.isNaN(value) ? EM_DASH : `${formatDecimal(value, maximumFractionDigits)} ${unit}`;

/** Falls back to an em dash so empty spec fields never render as an empty cell. */
export const formatText = (value: string | null | undefined): string =>
  value === null || value === undefined || value.trim() === '' ? EM_DASH : value;
